import * as React from 'react'
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import { Check, ChevronRight, Circle } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Icon3DOr } from '@/components/icons3d/Icon3D'
import { LUCIDE_TO_3D } from '@/components/icons3d/icons3d'
import { useSkin } from '@/theme/skin'

const MENU_CONTENT =
  'z-50 min-w-[8rem] max-w-[calc(100vw-24px)] overflow-y-auto overflow-x-hidden overscroll-contain rounded-surface border border-line bg-surface p-1 text-ink shadow-popover animate-pop-in-shift'

const MENU_ITEM =
  'relative flex cursor-pointer select-none items-center gap-2 rounded-control px-2 py-1.5 text-body outline-none transition-colors duration-150 ease-out focus:bg-accent data-[state=open]:bg-accent data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 max-sm:min-h-11 [@media(pointer:coarse)]:min-h-11'

/** Fluent 3D skin: a menu item's leading Lucide icon becomes its 3D counterpart. */
function MenuIcons3D({ children }) {
  const skin = useSkin()
  if (skin !== 'fluent3d') return children
  return React.Children.map(children, (child) => {
    const name = React.isValidElement(child) ? LUCIDE_TO_3D.get(child.type) : null
    return name ? <Icon3DOr name={name} size={20} className="-my-0.5">{child}</Icon3DOr> : child
  })
}

const DropdownMenu = DropdownMenuPrimitive.Root

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger

const DropdownMenuGroup = DropdownMenuPrimitive.Group

const DropdownMenuPortal = DropdownMenuPrimitive.Portal

const DropdownMenuSub = DropdownMenuPrimitive.Sub

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup

const DropdownMenuSubTrigger = React.forwardRef(({ className, inset, children, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(MENU_ITEM, 'cursor-default', inset && 'pl-8', className)}
    {...props}
  >
    <MenuIcons3D>{children}</MenuIcons3D>
    <ChevronRight className="ml-auto" />
  </DropdownMenuPrimitive.SubTrigger>
))
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName

const DropdownMenuSubContent = React.forwardRef(({ className, ...props }, ref) => (
  // Portal-rendered, matching DropdownMenuContent below and Radix's own docs
  // pattern for nested menus: without it, SubContent renders as a DOM child
  // of the parent Content, which carries the popper's own `animate-pop-in-shift`.
  // That animation ends holding a resting `transform` (translateY(0) scale(1),
  // via `animation-fill-mode: both`), and per spec ANY transform - including an
  // identity one - makes its element a containing block for `position: fixed`
  // descendants. Combined with the parent's `overflow-y-auto overflow-x-hidden`,
  // the un-portaled submenu was clipped to invisibility even though its own
  // geometry/opacity/pointer-events all computed correctly - real Chrome bug,
  // not a headless-only artifact (same CSS containing-block rules apply in a
  // headed browser).
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.SubContent ref={ref} className={cn(MENU_CONTENT, className)} {...props} />
  </DropdownMenuPrimitive.Portal>
))
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName

const DropdownMenuContent = React.forwardRef(({ className, sideOffset = 4, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        MENU_CONTENT,
        'max-h-[var(--radix-dropdown-menu-content-available-height)]',
        className
      )}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
))
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName

const DropdownMenuItem = React.forwardRef(({ className, inset, children, asChild, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    asChild={asChild}
    className={cn(MENU_ITEM, inset && 'pl-8', className)}
    {...props}
  >
    {/* asChild renders the one child element as the item: swap icons inside it, never wrap it. */}
    {asChild && React.isValidElement(children)
      ? React.cloneElement(children, undefined, <MenuIcons3D>{children.props.children}</MenuIcons3D>)
      : <MenuIcons3D>{children}</MenuIcons3D>}
  </DropdownMenuPrimitive.Item>
))
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName

const DropdownMenuCheckboxItem = React.forwardRef(({ className, children, checked, ...props }, ref) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    className={cn(MENU_ITEM, 'cursor-default pl-8', className)}
    checked={checked}
    {...props}
  >
    <span className="absolute left-2 flex size-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Check className="size-4" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.CheckboxItem>
))
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName

const DropdownMenuRadioItem = React.forwardRef(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    className={cn(MENU_ITEM, 'cursor-default pl-8', className)}
    {...props}
  >
    <span className="absolute left-2 flex size-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Circle className="size-2 fill-current" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.RadioItem>
))
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName

const DropdownMenuLabel = React.forwardRef(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn('px-2 py-1.5 text-caption font-medium text-ink-2', inset && 'pl-8', className)}
    {...props}
  />
))
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName

const DropdownMenuSeparator = React.forwardRef(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-line', className)}
    {...props}
  />
))
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName

const DropdownMenuShortcut = ({ className, ...props }) => (
  <span className={cn('ml-auto text-caption tracking-widest text-ink-placeholder', className)} {...props} />
)
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut'

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
}
